import React, { useState, useMemo, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Button, Modal, AutoComplete, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import _ from "lodash";
import { searchForGroup } from "../../actions";
import { ContentTable } from "@authzsvc/common-react-components";

const { Text } = Typography;

const GroupTable = ({ dataSource, searchLoading, handleSelect }) => {
  const modalColumns = [
    { title: "Name", dataIndex: "displayName", searchable: true },
    {
      title: "Group ID",
      dataIndex: "groupIdentifier",
      searchable: true,
      render: (val, record) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          {val ? <Text code>{val}</Text> : <Text type="secondary">N/A</Text>}
          <Button
            id="selectGroupButton"
            color="green"
            variant="filled"
            icon={<PlusOutlined />}
            onClick={() => handleSelect(record)}
          />
        </div>
      ),
    },
  ];

  return (
    <ContentTable
      style={{ marginTop: 10 }}
      loading={searchLoading}
      rowKey="id"
      pagination={{ pageSize: 10 }}
      columns={modalColumns}
      dataSource={dataSource}
    />
  );
};

GroupTable.propTypes = {
  dataSource: PropTypes.array,
  searchLoading: PropTypes.bool,
  handleSelect: PropTypes.func,
};

const GroupSearchModal = ({
  onSelect,
  title,
  searchForGroup,
  open,
  onOpenChange,
}) => {
  const [searchLoading, setSearchLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);

  useEffect(() => {
    if (open) {
      setDataSource([]);
      setSearchLoading(false);
    }
  }, [open]);

  const clear = useCallback(() => onOpenChange?.(false), [onOpenChange]);

  const handleSelect = useCallback(
    (record) => {
      onSelect?.(record);
      clear();
    },
    [onSelect, clear],
  );

  const debouncedSearch = useMemo(
    () =>
      _.debounce(async (value = "") => {
        const q = value.trim();
        if (q.length < 2) {
          setDataSource([]);
          setSearchLoading(false);
          return;
        }
        setSearchLoading(true);
        const response = await searchForGroup(
          {
            filter: `groupIdentifier:startswith:${q}`,
            sort: "groupIdentifier",
          },
          100,
        );
        setSearchLoading(false);
        const rows = response?.payload?.data ?? [];
        setDataSource(Array.isArray(rows) ? rows : []);
      }, 400),
    [searchForGroup],
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  return (
    <>
      <Modal
        centered
        title={title}
        open={!!open}
        onCancel={clear}
        footer={null}
        width={700}
        closable
      >
        <AutoComplete
          onChange={debouncedSearch}
          onSearch={debouncedSearch}
          allowClear
          options={[]}
          style={{ width: "100%" }}
          placeholder="Type at least 2 characters of the group's identifier."
        />
        <GroupTable
          dataSource={dataSource}
          searchLoading={searchLoading}
          handleSelect={handleSelect}
        />
      </Modal>
    </>
  );
};

GroupSearchModal.propTypes = {
  onSelect: PropTypes.func,
  searchForGroup: PropTypes.func,
  title: PropTypes.string,
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
};

export default connect(() => ({}), { searchForGroup })(GroupSearchModal);
